import React, { useState } from 'react'
import { TextField, TextArea, SegmentedToggle, Button, Banner } from '../ui'
import { formatTimeHorizon } from './timeHorizon.js'

const TYPE_OPTIONS = [
  { value: 'delivery', label: 'Delivery' },
  { value: 'development', label: 'Development' },
]

const HORIZON_TYPE_OPTIONS = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' },
]

const MONTH_OPTIONS = [
  { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
  { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
  { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
  { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' },
]

const QUARTER_OPTIONS = [
  { value: 1, label: 'Q1' }, { value: 2, label: 'Q2' }, { value: 3, label: 'Q3' }, { value: 4, label: 'Q4' },
]

function currentYear() { return new Date().getFullYear() }

function yearOptions() {
  const y = currentYear()
  return Array.from({ length: 5 }, (_, i) => y - 1 + i)
}

function initHorizonType(th) {
  if (!th || typeof th === 'string') return 'quarterly'
  return th.type || 'quarterly'
}

function initHorizonYear(th) {
  if (th && typeof th === 'object' && th.year) return th.year
  return currentYear()
}

function initHorizonMonth(th) {
  if (th && typeof th === 'object' && th.month) return th.month
  return new Date().getMonth() + 1
}

function initHorizonQuarter(th) {
  if (th && typeof th === 'object' && th.quarter) return th.quarter
  return Math.ceil((new Date().getMonth() + 1) / 3)
}

export function GoalForm({ initial = {}, onSave, onCancel, ownerUserId = null }) {
  const [title, setTitle] = useState(initial.title || '')
  const [description, setDescription] = useState(initial.description || '')
  const [goalType, setGoalType] = useState(initial.goalType || 'delivery')
  const [horizonType, setHorizonType] = useState(initHorizonType(initial.timeHorizon))
  const [horizonYear, setHorizonYear] = useState(initHorizonYear(initial.timeHorizon))
  const [horizonMonth, setHorizonMonth] = useState(initHorizonMonth(initial.timeHorizon))
  const [horizonQuarter, setHorizonQuarter] = useState(initHorizonQuarter(initial.timeHorizon))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const isEdit = !!initial.goalId

  function buildTimeHorizon() {
    const th = { type: horizonType, year: horizonYear }
    if (horizonType === 'monthly') th.month = horizonMonth
    if (horizonType === 'quarterly') th.quarter = horizonQuarter
    return th
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setError(null)
    if (!title.trim()) { setError('Title is required.'); return }

    const body = {
      title: title.trim(),
      description: description.trim(),
      goalType,
      timeHorizon: JSON.stringify(buildTimeHorizon()),
    }
    if (ownerUserId) body.ownerUserId = ownerUserId

    setSaving(true)
    try {
      await onSave(body)
    } catch (err) {
      setError(err?.message || 'Something went wrong.')
      setSaving(false)
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}
    >
      <TextField
        label="Title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="What's the objective?"
        maxLength={100}
        required
      />

      <TextArea
        label="Description"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Add context — what does success look like?"
        maxLength={1000}
        rows={3}
      />

      <div className="field">
        <label className="label">Type</label>
        <SegmentedToggle
          ariaLabel="Goal type"
          options={TYPE_OPTIONS}
          value={goalType}
          onChange={setGoalType}
        />
      </div>

      <div>
        <label style={styles.fieldLabel}>Time horizon</label>
        <div style={styles.horizonRow}>
          <SegmentedToggle
            ariaLabel="Time horizon type"
            options={HORIZON_TYPE_OPTIONS}
            value={horizonType}
            onChange={setHorizonType}
          />
        </div>
        <div style={styles.horizonPickers}>
          {horizonType === 'monthly' && (
            <select
              style={styles.select}
              value={horizonMonth}
              onChange={(e) => setHorizonMonth(Number(e.target.value))}
              aria-label="Month"
            >
              {MONTH_OPTIONS.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          )}
          {horizonType === 'quarterly' && (
            <select
              style={styles.select}
              value={horizonQuarter}
              onChange={(e) => setHorizonQuarter(Number(e.target.value))}
              aria-label="Quarter"
            >
              {QUARTER_OPTIONS.map((q) => (
                <option key={q.value} value={q.value}>{q.label}</option>
              ))}
            </select>
          )}
          <select
            style={styles.select}
            value={horizonYear}
            onChange={(e) => setHorizonYear(Number(e.target.value))}
            aria-label="Year"
          >
            {yearOptions().map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <span style={styles.horizonPreview}>{formatTimeHorizon(buildTimeHorizon())}</span>
        </div>
      </div>

      {error ? <Banner tone="error">{error}</Banner> : null}

      <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
        <Button variant="secondary" onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button
          type="submit"
          loading={saving}
          loadingLabel="Saving…"
        >
          {isEdit ? 'Save changes' : 'Create goal'}
        </Button>
      </div>
    </form>
  )
}

const styles = {
  fieldLabel: { fontSize: '12px', fontWeight: 500, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' },
  horizonRow: { marginBottom: '8px' },
  horizonPickers: { display: 'flex', gap: '8px', alignItems: 'center' },
  select: {
    padding: '6px 8px',
    borderRadius: '6px',
    border: '1px solid var(--border)',
    background: 'var(--surface)',
    color: 'var(--text-primary)',
    fontSize: '13px',
    fontFamily: 'inherit',
  },
  horizonPreview: { fontSize: '12px', color: 'var(--text-secondary)', marginLeft: '4px' },
}
